import React, { useEffect, useRef } from 'react';

/**
 * 滚动哨兵：放置在列表底部，进入视口缓冲区时通过 onLoadMore 追加下一批。
 *
 * IntersectionObserver 单独使用会在两种情形漏触发：
 * 1. 快速滚动一帧穿过 rootMargin，浏览器合并状态只递交终态 false。
 * 2. 哨兵保持 intersecting 期间 IO 不再回调，单批若未推出缓冲区则加载链停摆。
 *
 * 因此组合三路触发：IO（稳态主路径）+ scroll（rAF 节流，覆盖 IO 合并丢失）
 * + drive（render 后再核，驱动级联加载）。建议批次数由 checkRef 估算。
 *
 * 调用方契约：
 * - onLoadMore 必须幂等（三路可能同帧并发）。
 * - 不可用 React.memo 包装本组件：drive 依赖父 render 带动子 render。
 */
interface IScrollSentinelProps {
  /** 为 false 时仅停止触发，不卸载哨兵 DOM，避免切回 true 时 IO 观察悬空节点。 */
  hasMore: boolean;
  /** 触底加载回调，必须幂等。count 为建议批次数，由哨兵根据被甩出的视口数估算。 */
  onLoadMore: (count?: number) => void;
  /** 触发缓冲区高度（px），默认 400。同时作为 IO rootMargin 与 scroll/drive 阈值。 */
  bufferPx?: number;
  /** 自定义滚动容器；不传时自动向上找最近的 overflowY auto/scroll 祖先。 */
  root?: Element | null;
  /** 哨兵需有非零 bounding box（如 height: 1px），由调用方保证。 */
  className?: string;
}

const DEFAULT_BUFFER_PX = 400;
const DEFAULT_CLASS_NAME = 'dbi-report-search__sentinel';

const findScrollableAncestor = (
  start: HTMLElement | null,
): HTMLElement | null => {
  let cur: HTMLElement | null = start;
  while (cur && cur !== document.body && cur !== document.documentElement) {
    const cs = window.getComputedStyle(cur);
    if (
      (cs.overflowY === 'auto' || cs.overflowY === 'scroll') &&
      cur.scrollHeight > cur.clientHeight + 1
    ) {
      return cur;
    }
    cur = cur.parentElement;
  }
  return null;
};

const ScrollSentinel: React.FC<IScrollSentinelProps> = ({
  hasMore,
  onLoadMore,
  bufferPx = DEFAULT_BUFFER_PX,
  root = null,
  className = DEFAULT_CLASS_NAME,
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  const hasMoreRef = useRef<boolean>(hasMore);
  const onLoadMoreRef = useRef<(count?: number) => void>(onLoadMore);
  const bufferPxRef = useRef<number>(bufferPx);
  hasMoreRef.current = hasMore;
  onLoadMoreRef.current = onLoadMore;
  bufferPxRef.current = bufferPx;

  const effectiveRootRef = useRef<Element | null>(null);
  const justFiredRef = useRef<boolean>(false);

  const checkRef = useRef<() => void>(() => {});
  checkRef.current = (): void => {
    if (!hasMoreRef.current) return;
    const node = sentinelRef.current;
    if (!node) return;
    const buffer = bufferPxRef.current;
    const effectiveRoot = effectiveRootRef.current;
    const rect = node.getBoundingClientRect();

    let topRelative: number;
    let viewportHeight: number;
    if (effectiveRoot instanceof Element) {
      const rootRect = effectiveRoot.getBoundingClientRect();
      topRelative = rect.top - rootRect.top;
      viewportHeight = effectiveRoot.clientHeight;
    } else {
      topRelative = rect.top;
      viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    }

    // 不限制 topRelative >= 0：fast scroll 一帧可让其变负（哨兵已被滚过），仍需触发。
    if (topRelative >= viewportHeight + buffer) return;

    const gapPx = viewportHeight + buffer - topRelative;
    const count = Math.max(1, Math.ceil(gapPx / viewportHeight));
    justFiredRef.current = true;
    onLoadMoreRef.current(count);
  };

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const effectiveRoot: Element | null =
      root instanceof Element ? root : findScrollableAncestor(node.parentElement);
    effectiveRootRef.current = effectiveRoot;

    let rafId: number | null = null;
    const scrollHandler = (): void => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        checkRef.current();
      });
    };

    const observer = new IntersectionObserver(() => checkRef.current(), {
      root: effectiveRoot,
      rootMargin: `${bufferPx}px 0px`,
    });
    observer.observe(node);

    // scroll 必须挂在 effectiveRoot 上：容器内部滚动不会冒泡到 window。
    const scrollTarget: EventTarget = effectiveRoot instanceof Element ? effectiveRoot : window;
    scrollTarget.addEventListener('scroll', scrollHandler, { passive: true });

    return () => {
      observer.disconnect();
      scrollTarget.removeEventListener('scroll', scrollHandler);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [root, bufferPx]);

  // drive：仅在上一次 check 真触发过加载时再核位置，自动级联直至哨兵被推出缓冲区。
  useEffect(() => {
    if (!justFiredRef.current) return;
    justFiredRef.current = false;
    checkRef.current();
  });

  return <div ref={sentinelRef} className={className} />;
};

export default ScrollSentinel;
